// The formula language (PRD §7.5).
// References: node handles. Operators: + - * / and parentheses.
// Functions: min, max, floor, ceil, round, abs, sum(...), lag(metric, months), happened(event).
// The parser lives here so that both the rules (VB-1, VB-5) and the engine use the same code.

// Local aliases: Math property lookups are slow in some hosts (for example Jest's VM context).
const { abs: mathAbs, ceil: mathCeil, floor: mathFloor, max: mathMax, min: mathMin, round: mathRound } = Math;

export interface FormulaCtx {
	/** Grid index of the month being computed. */
	t: number;
	metric(handle: string, t: number): number;
	happened(handle: string, t: number): boolean;
}

export type Compiled = (ctx: FormulaCtx) => number;

export interface ParsedFormula {
	fn: Compiled;
	/** Same-month metric references. */
	refs: string[];
	/** Metrics referenced through lag(). */
	lagRefs: string[];
	/** Events referenced through happened(). */
	eventRefs: string[];
}

export const FORMULA_FUNCTIONS = ["min", "max", "floor", "ceil", "round", "abs", "sum", "lag", "happened"] as const;
const FUNCS = new Set<string>(FORMULA_FUNCTIONS);

type Tok = { k: "num"; v: number; at: number } | { k: "id"; v: string; at: number } | { k: "op"; v: string; at: number };

function tokenize(src: string): Tok[] {
	const out: Tok[] = [];
	let i = 0;
	while (i < src.length) {
		const c = src[i];
		if (/\s/.test(c)) {
			i++;
			continue;
		}
		const num = /^(\d+\.?\d*(e[+-]?\d+)?|\.\d+(e[+-]?\d+)?)/i.exec(src.slice(i));
		if (num) {
			out.push({ k: "num", v: parseFloat(num[0]), at: i });
			i += num[0].length;
			continue;
		}
		const id = /^[a-zA-Z_][a-zA-Z0-9_]*/.exec(src.slice(i));
		if (id) {
			out.push({ k: "id", v: id[0], at: i });
			i += id[0].length;
			continue;
		}
		if ("+-*/(),".includes(c)) {
			out.push({ k: "op", v: c, at: i });
			i++;
			continue;
		}
		throw new Error(`Unexpected "${c}" at position ${i + 1}.`);
	}
	return out;
}

export interface KnownNodes {
	metrics: Set<string>;
	events: Set<string>;
}

/**
 * Parse and compile a formula. With `known`, unknown names are errors.
 * Throws an Error with a plain-language message when the formula is not valid.
 */
export function parseFormula(src: string, known?: KnownNodes): ParsedFormula {
	if (!src.trim()) throw new Error("The formula is empty.");
	const toks = tokenize(src);
	let p = 0;
	const refs = new Set<string>();
	const lagRefs = new Set<string>();
	const eventRefs = new Set<string>();

	const peek = () => toks[p];
	const isOp = (v: string) => peek()?.k === "op" && peek()!.v === v;
	const expect = (v: string) => {
		if (!isOp(v)) throw new Error(peek() ? `Expected "${v}" at position ${peek()!.at + 1}.` : `Expected "${v}" at the end.`);
		p++;
	};

	function expr(): Compiled {
		let left = term();
		while (isOp("+") || isOp("-")) {
			const op = toks[p++].v;
			const a = left;
			const b = term();
			left = op === "+" ? (c) => a(c) + b(c) : (c) => a(c) - b(c);
		}
		return left;
	}
	function term(): Compiled {
		let left = unary();
		while (isOp("*") || isOp("/")) {
			const op = toks[p++].v;
			const a = left;
			const b = unary();
			left =
				op === "*"
					? (c) => a(c) * b(c)
					: (c) => {
							const d = b(c);
							return d === 0 ? 0 : a(c) / d;
						};
		}
		return left;
	}
	function unary(): Compiled {
		if (isOp("-")) {
			p++;
			const a = unary();
			return (c) => -a(c);
		}
		if (isOp("+")) {
			p++;
			return unary();
		}
		return primary();
	}
	function ident(): string {
		const t = peek();
		if (t?.k !== "id") throw new Error("Expected a node name.");
		p++;
		return t.v;
	}
	function checkMetric(id: string) {
		if (known && !known.metrics.has(id)) {
			if (known.events.has(id)) throw new Error(`"${id}" is an event. Use happened(${id}) to use it in a formula.`);
			throw new Error(`Unknown metric "${id}".`);
		}
	}
	function primary(): Compiled {
		const t = peek();
		if (!t) throw new Error("The formula ends too early.");
		if (t.k === "num") {
			p++;
			const v = t.v;
			return () => v;
		}
		if (t.k === "op" && t.v === "(") {
			p++;
			const e = expr();
			expect(")");
			return e;
		}
		if (t.k === "id") {
			p++;
			if (isOp("(")) {
				if (!FUNCS.has(t.v)) throw new Error(`Unknown function "${t.v}". Use one of: ${FORMULA_FUNCTIONS.join(", ")}.`);
				p++;
				if (t.v === "lag") {
					const id = ident();
					checkMetric(id);
					expect(",");
					const nTok = peek();
					if (nTok?.k !== "num") throw new Error("lag() needs a number of months, for example lag(revenue, 3).");
					p++;
					expect(")");
					lagRefs.add(id);
					const k = mathMax(1, mathRound(nTok.v));
					return (c) => c.metric(id, c.t - k);
				}
				if (t.v === "happened") {
					const id = ident();
					if (known && !known.events.has(id)) throw new Error(`Unknown event "${id}".`);
					expect(")");
					eventRefs.add(id);
					return (c) => (c.happened(id, c.t) ? 1 : 0);
				}
				const args: Compiled[] = [expr()];
				while (isOp(",")) {
					p++;
					args.push(expr());
				}
				expect(")");
				const a0 = args[0];
				switch (t.v) {
					case "floor":
						return (c) => mathFloor(a0(c));
					case "ceil":
						return (c) => mathCeil(a0(c));
					case "round":
						return (c) => mathRound(a0(c));
					case "abs":
						return (c) => mathAbs(a0(c));
					case "min":
						return (c) => {
							let v = a0(c);
							for (let i = 1; i < args.length; i++) v = mathMin(v, args[i](c));
							return v;
						};
					case "max":
						return (c) => {
							let v = a0(c);
							for (let i = 1; i < args.length; i++) v = mathMax(v, args[i](c));
							return v;
						};
					default:
						return (c) => {
							let v = 0;
							for (const a of args) v += a(c);
							return v;
						};
				}
			}
			checkMetric(t.v);
			refs.add(t.v);
			const id = t.v;
			return (c) => c.metric(id, c.t);
		}
		throw new Error(`Unexpected "${t.v}" at position ${t.at + 1}.`);
	}

	const fn = expr();
	if (p < toks.length) throw new Error(`Unexpected "${toks[p].v}" at position ${toks[p].at + 1}.`);
	return { fn, refs: [...refs], lagRefs: [...lagRefs], eventRefs: [...eventRefs] };
}

/** The error message for a formula, or null when it parses. */
export function formulaError(src: string, known?: KnownNodes): string | null {
	try {
		parseFormula(src, known);
		return null;
	} catch (e) {
		return (e as Error).message;
	}
}

/** Every handle a formula references (same-month, lagged, and events), or null when it does not parse. */
export function formulaRefs(src: string): { refs: string[]; lagRefs: string[]; eventRefs: string[] } | null {
	try {
		const { refs, lagRefs, eventRefs } = parseFormula(src);
		return { refs, lagRefs, eventRefs };
	} catch {
		return null;
	}
}

/**
 * Order nodes so each comes after the nodes it depends on in the same month.
 * `onCycle` holds the nodes that are on a cycle; `cyclic` also holds the nodes that depend on one.
 * Nodes in `cyclic` are left out of `order`. Dependencies on unknown nodes are ignored.
 */
export function sameMonthOrder(deps: Map<string, string[]>): { order: string[]; cyclic: Set<string>; onCycle: Set<string> } {
	// Tarjan's strongly connected components.
	let index = 0;
	const idx = new Map<string, number>();
	const low = new Map<string, number>();
	const onStack = new Set<string>();
	const stack: string[] = [];
	const onCycle = new Set<string>();
	const strong = (v: string) => {
		idx.set(v, index);
		low.set(v, index);
		index++;
		stack.push(v);
		onStack.add(v);
		for (const w of deps.get(v) ?? []) {
			if (!deps.has(w)) continue;
			if (!idx.has(w)) {
				strong(w);
				low.set(v, mathMin(low.get(v)!, low.get(w)!));
			} else if (onStack.has(w)) low.set(v, mathMin(low.get(v)!, idx.get(w)!));
		}
		if (low.get(v) === idx.get(v)) {
			const comp: string[] = [];
			let w: string;
			do {
				w = stack.pop()!;
				onStack.delete(w);
				comp.push(w);
			} while (w !== v);
			if (comp.length > 1 || (deps.get(v) ?? []).includes(v)) for (const c of comp) onCycle.add(c);
		}
	};
	for (const v of deps.keys()) if (!idx.has(v)) strong(v);

	const order: string[] = [];
	const cyclic = new Set<string>(onCycle);
	const done = new Set<string>();
	const visit = (v: string): boolean => {
		if (cyclic.has(v)) return false;
		if (done.has(v)) return true;
		done.add(v);
		let ok = true;
		for (const w of deps.get(v) ?? []) if (deps.has(w) && !visit(w)) ok = false;
		if (ok) order.push(v);
		else cyclic.add(v);
		return ok;
	};
	for (const v of deps.keys()) visit(v);
	return { order, cyclic, onCycle };
}

/**
 * History for a calculated metric: its formula evaluated in every past month where all of its
 * inputs have a value. Inputs that are themselves calculated are resolved first.
 */
export function formulaHistory(
	handle: string,
	nodes: Record<string, { kind: string; forecast?: { formula?: string | null }; observed?: { month: string; value: number }[]; happened?: string | null }>,
): { month: string; value: number }[] {
	const toAbs = (m: string) => Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7)) - 1;
	const fromAbs = (a: number) => `${mathFloor(a / 12)}-${String((a % 12) + 1).padStart(2, "0")}`;
	const memo = new Map<string, Map<number, number>>();
	const visiting = new Set<string>();
	const series = (nid: string): Map<number, number> => {
		const hit = memo.get(nid);
		if (hit) return hit;
		const n = nodes[nid];
		const out = new Map<number, number>();
		if (!n || n.kind !== "metric" || visiting.has(nid)) return out;
		const formula = n.forecast?.formula;
		if (!formula) {
			for (const o of n.observed ?? []) out.set(toAbs(o.month), o.value);
			memo.set(nid, out);
			return out;
		}
		visiting.add(nid);
		try {
			const f = parseFormula(formula);
			const inputs = [...f.refs, ...f.lagRefs];
			const maps = new Map(inputs.map((r) => [r, series(r)]));
			const first = f.refs[0] ?? f.lagRefs[0];
			const months = first ? [...(maps.get(first) ?? new Map<number, number>()).keys()] : [];
			for (const t of months.sort((a, b) => a - b)) {
				let ok = true;
				const val = (r: string, tt: number) => {
					const v = maps.get(r)?.get(tt);
					if (v === undefined) ok = false;
					return v ?? 0;
				};
				const v = f.fn({
					t,
					metric: (r, tt) => val(r, tt),
					happened: (e, tt) => {
						const h = nodes[e]?.happened;
						return !!h && toAbs(h) <= tt;
					},
				});
				if (ok && Number.isFinite(v)) out.set(t, v);
			}
		} catch {
			/* an invalid formula has no history */
		}
		visiting.delete(nid);
		memo.set(nid, out);
		return out;
	};
	return [...series(handle).entries()].sort((a, b) => a[0] - b[0]).map(([t, value]) => ({ month: fromAbs(t), value }));
}
